import * as ChatApi from '../../connection/chat';
import {
	ChatPost,
	ChatRoom,
	GeneralEventsMessage,
} from '../reducers/chat';
import { ConnectedUser } from '../../connection/socket';
import { GreenroomUser } from '../reducers/session';
import { ChatRoomStatus } from '../types/types';

interface AsyncAction {
	type: string;
	promise: Promise<any>
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Action {
	type: string;
	payload: any;
}

export interface ChatMessage {
	token: string,
	meeting: number,
	message: string,
	attachment?: string,
	room?: number,
	recipient?: number,
	mentions?: number[]
}

export const CLEAR_CHAT_DIRECT_CHAT = 'CLEAR_CHAT_DIRECT_CHAT';
export function clearChatDirectChat(): Action {
	return {
		type: CLEAR_CHAT_DIRECT_CHAT,
		payload: null,
	};
}

export const CLEAR_CHAT_DIRECT_CHAT_KEY = 'CLEAR_CHAT_DIRECT_CHAT_KEY';
export function clearChatDirectChatId({ key, all }: {key: string, all?: boolean}): Action {
	return {
		type: CLEAR_CHAT_DIRECT_CHAT_KEY,
		payload: { key, all },
	};
}

export const LOAD_CHAT_DIRECT_CHAT = 'LOAD_CHAT_DIRECT_CHAT';
export function loadChatPosts(
	token: string,
	meeting: number,
	recipient?: number,
	lastId?: number,
): AsyncAction {
	return {
		type: LOAD_CHAT_DIRECT_CHAT,
		promise: ChatApi.LoadDirectChat(token, meeting, recipient, lastId),
	};
}

export const LOAD_ROOM_CHAT = 'LOAD_ROOM_CHAT';
export function loadRoomChat(token: string, meeting: number): AsyncAction {
	return {
		type: LOAD_ROOM_CHAT,
		promise: ChatApi.LoadChatRooms(token, meeting),
	};
}

export const LOAD_CHAT_ROOM_MESSAGES = 'LOAD_CHAT_ROOM_MESSAGES';
export function loadChatRoomMessages(messages: ChatPost[]) {
	return {
		type: LOAD_CHAT_ROOM_MESSAGES,
		payload: messages,
	};
}

export const CLEAR_CHAT_ROOM_MESSAGES = 'CLEAR_CHAT_ROOM_MESSAGES';
export function clearChatRoomMessages(roomId: number, all: boolean = false) {
	return {
		type: CLEAR_CHAT_ROOM_MESSAGES,
		payload: {
			roomId,
			all,
		},
	};
}

export const DELETE_CHAT_ROOM = 'DELETE_CHAT_ROOM';
export function deleteChatRoom(roomId: number) {
	return {
		type: DELETE_CHAT_ROOM,
		payload: roomId,
	};
}

export function softDeleteChatRoom(channel: number, room: number): AsyncAction {
  return {
    type: DELETE_CHAT_ROOM,
    promise: ChatApi.DeleteRoom(channel, room),
  };
}

export function softDeleteChatForMeetingAndRoom(
	token: string,
	meeting: number,
	room: number,
	userId: number,
): AsyncAction {
	return {
		type: LOAD_ROOM_CHAT,
		promise: ChatApi.SoftDeleteChatMessagesForMeetingAndRoom(token, meeting, room, userId),
	};
}

export const POST_CHAT = 'POST_CHAT';
export function postChat(mess: ChatMessage) {
	const {
		token,
		meeting,
		message,
		attachment,
		room,
		recipient,
	} = mess;
	return {
		type: POST_CHAT,
		promise: ChatApi.PostChat(token, meeting, message, attachment, room, recipient),
	};
}

export const CREATE_ROOM = 'CREATE_ROOM';
export function createRoom(token: string, meeting: number, name: string, participants: number[]) {
	return {
		type: CREATE_ROOM,
		promise: ChatApi.CreateRoom(token, meeting, name, participants),
	};
}

export const NEW_MESSAGE = 'NEW_MESSAGE';
export function newMessage(message: ChatPost) {
	return {
		type: NEW_MESSAGE,
		payload: message,
	};
}

export const NEW_MESSAGE_INTERNAL = 'NEW_MESSAGE_INTERNAL';
export function newMessageInternal(message: ChatPost) {
	return {
		type: NEW_MESSAGE,
		payload: message,
	};
}

export const NEW_ROOM = 'NEW_ROOM';
export function newRoom(room: ChatRoom) {
	return {
		type: NEW_ROOM,
		payload: room,
	};
}

export const SET_SELECTED_USER = 'SET_SELECTED_USER';
export function setSelectedUser(user: ConnectedUser | null) {
	return {
		type: SET_SELECTED_USER,
		payload: user,
	};
}

export const SET_SELECTED_ROOM = 'SET_SELECTED_ROOM';
export function setSelectedRoom(room: number | null) {
	return {
		type: SET_SELECTED_ROOM,
		payload: room,
	};
}

export const CREATE_CONVO = 'CREATE_CONVO';
export function createConvo(user: ConnectedUser | GreenroomUser, connUser: ConnectedUser) {
	return {
		type: CREATE_CONVO,
		payload: [user, connUser],
	};
}

export const SET_DIRECT_MESSAGES_SEEN = 'SET_DIRECT_MESSAGES_SEEN';
export function setDirectMessagesSeen(
	token: string,
	meeting: number,
	authorOrRoomId: number,
	messages: number[],
) {
	return {
		type: SET_DIRECT_MESSAGES_SEEN,
		promise: ChatApi.MessagesSeen(
			token,
			meeting,
			authorOrRoomId,
			ChatRoomStatus.Direct,
			messages,
		),
	};
}

export const SET_ROOM_MESSAGES_SEEN = 'SET_ROOM_MESSAGES_SEEN';
export function setRoomMessagesSeen(
	token: string,
	meeting: number,
	authorOrRoomId: number,
	messages: number[],
) {
	return {
		type: SET_ROOM_MESSAGES_SEEN,
		promise: ChatApi.MessagesSeen(
			token,
			meeting,
			authorOrRoomId,
			ChatRoomStatus.Presenter,
			messages,
		),
	};
}

export const SET_MODERATED_LEGACY_COMMENTS_SEEN = 'SET_MODERATED_LEGACY_COMMENTS_SEEN';
export function setModeratedLegacyCommentsSeen() {
	return {
		type: SET_MODERATED_LEGACY_COMMENTS_SEEN,
		payload: null,
	};
}

export const SET_MODERATED_EVENTS_MESSAGES_SEEN = 'SET_MODERATED_EVENTS_MESSAGES_SEEN';
export function setModeratedEventsMessagesSeen(
	token: string,
	// eslint-disable-next-line camelcase
	meeting_id: string,
	chatMessages: number[],
	questions: number[],
	comments: number[],
) {
	return {
		type: SET_MODERATED_EVENTS_MESSAGES_SEEN,
		promise: ChatApi.UpdateModeratedEventsMessagesSeen(
			token,
			meeting_id,
			chatMessages,
			questions,
			comments,
		),
	};
}

export const SET_GR_USER = 'SET_GR_USER';
export function setGrUser(profile: number) {
	return {
		type: SET_GR_USER,
		payload: profile,
	};
}

export const GET_MODERATED_COMMENTS = 'GET_MODERATED_COMMENTS';
// eslint-disable-next-line camelcase
export function getModeratedComments(token: string, live_chat: number) {
	return {
		type: GET_MODERATED_COMMENTS,
		promise: ChatApi.GetModeratedComments(token, live_chat),
	};
}

export const GET_APPROVED_COMMENT = 'GET_APPROVED_COMMENT';
// eslint-disable-next-line camelcase
export function getApprovedComment(token: string, chat_message: number) {
	return {
		type: GET_APPROVED_COMMENT,
		promise: ChatApi.GetApprovedComment(token, chat_message),
	};
}

export const DELETE_COMMENT = 'DELETE_COMMENT';
// eslint-disable-next-line camelcase
export function deleteComment(chat_message: number) {
	return {
		type: DELETE_COMMENT,
		// eslint-disable-next-line camelcase
		payload: chat_message,
	};
}

export const UNDELETE_COMMENT = 'UNDELETE_COMMENT';
// eslint-disable-next-line camelcase
export function undeleteComment(chat_message: number) {
	return {
		type: UNDELETE_COMMENT,
		// eslint-disable-next-line camelcase
		payload: chat_message,
	};
}

export const ELEVATE_COMMENT = 'ELEVATE_COMMENT';
// eslint-disable-next-line camelcase
export function elevateComment(chat_message: number) {
	return {
		type: ELEVATE_COMMENT,
		// eslint-disable-next-line camelcase
		payload: chat_message,
	};
}

export const UNELEVATE_COMMENT = 'UNELEVATE_COMMENT';
// eslint-disable-next-line camelcase
export function unelevateComment(chat_message: number) {
	return {
		type: UNELEVATE_COMMENT,
		// eslint-disable-next-line camelcase
		payload: chat_message,
	};
}

export const VIEW_MODERATED_COMMENTS = 'VIEW_MODERATED_COMMMENTS';
export function viewModeratedComments(shouldView: boolean) {
	return {
		type: VIEW_MODERATED_COMMENTS,
		payload: shouldView,
	};
}

export const CREATE_MODERATED_EVENTS_MESSAGES = 'CREATE_MODERATED_EVENTS_MESSAGES';
export function createModeratedEventsMessages(
	key: string,
	eventsMessage: GeneralEventsMessage,
) {
	return {
		type: CREATE_MODERATED_EVENTS_MESSAGES,
		payload: {
			key,
			eventsMessage,
		},
	};
}

export const DELETE_MODERATED_EVENTS_MESSAGES = 'DELETE_MODERATED_EVENTS_MESSAGES';
export function deleteModeratedEventsMessages(key: string) {
	return {
		type: DELETE_MODERATED_EVENTS_MESSAGES,
		payload: key,
	};
}

export const GET_PRESENTER_MESSAGES = 'GET_PRESENTER_MESSAGES';
// eslint-disable-next-line camelcase
export function getPresenterMessages(token: string, meeting_id: string) {
	return {
		type: GET_PRESENTER_MESSAGES,
		promise: ChatApi.GetPresenterMessages(token, meeting_id),
	};
}

export const REMOVE_CHAT_MESSAGE = 'REMOVE_CHAT_MESSAGE';
export function removeChatMessage(messageId: number, roomId: number | undefined, dmKey: string) {
	return {
		type: REMOVE_CHAT_MESSAGE,
		payload: {
			messageId,
			roomId,
			dmKey,
		},
	};
}
